/**
 * Wizard — config-driven multi-step form for the cold-start tool.
 *
 * Iterates `WIZARD_CONFIG.steps` and renders each step's fields via the
 * shared `DynamicField` switch. Owns the per-step rendering, progress bar,
 * and Back/Continue navigation; profile state lives in the parent.
 */
import * as React from 'react';
import { ChevronLeft, ChevronRight, Calculator } from 'lucide-react';
import type { ProfileInput, WizardConfig } from '../types';
import { PALETTE_FOREST, PALETTE_AMBER } from '../tokens';
import { DynamicField } from './dynamic-field';

interface WizardProps {
  config:   WizardConfig;
  profile:  ProfileInput;
  onChange: <K extends keyof ProfileInput>(key: K, value: ProfileInput[K]) => void;
  onSubmit: () => void;
}

export function Wizard({ config, profile, onChange, onSubmit }: WizardProps) {
  const [stepIdx, setStepIdx] = React.useState(0);
  const totalSteps = config.steps.length;
  const step = config.steps[stepIdx]!;
  const isLast = stepIdx === totalSteps - 1;

  function goNext() {
    if (isLast) {
      onSubmit();
      return;
    }
    setStepIdx((s) => Math.min(s + 1, totalSteps - 1));
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: 'smooth' }),
    );
  }

  function goBack() {
    setStepIdx((s) => Math.max(s - 1, 0));
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: 'smooth' }),
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Progress ──────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            Your progress
          </span>
          <span
            className="font-mono tabular-nums text-xs font-medium"
            style={{ color: PALETTE_FOREST }}
          >
            {stepIdx + 1} / {totalSteps}
          </span>
        </div>
        <div className="flex gap-1.5 h-1">
          {Array.from({ length: totalSteps }).map((_, i) => {
            const state = i < stepIdx ? 'done' : i === stepIdx ? 'active' : 'pending';
            return (
              <div
                key={i}
                className="flex-1 rounded-full transition-colors duration-300"
                style={{
                  backgroundColor:
                    state === 'done'   ? PALETTE_FOREST :
                    state === 'active' ? PALETTE_AMBER  : 'hsl(var(--border))',
                }}
              />
            );
          })}
        </div>
      </div>

      {/* ── Step card ─────────────────────────────────────────────── */}
      <div
        key={stepIdx}
        className="rounded-2xl border border-border bg-background p-6 sm:p-7 shadow-sm animate-slide-up-fade"
      >
        <p
          className="text-[10px] font-bold uppercase tracking-widest mb-2"
          style={{ color: PALETTE_AMBER }}
        >
          {step.eyebrow}
        </p>
        <h2 className="font-display font-semibold text-xl sm:text-2xl text-foreground tracking-tight leading-tight">
          {step.title}
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed">
          {step.subtitle}
        </p>

        <div className="mt-6 space-y-5">
          {step.fields.map((field) => (
            <DynamicField key={field.id} field={field} profile={profile} onChange={onChange} />
          ))}
        </div>
      </div>

      {/* ── Navigation ────────────────────────────────────────────── */}
      <div className={`flex gap-3 ${stepIdx > 0 ? 'justify-between' : 'justify-end'}`}>
        {stepIdx > 0 && (
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-medium border border-border bg-background text-foreground hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Back
          </button>
        )}
        <button
          type="button"
          onClick={goNext}
          className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-md active:scale-95"
          style={{ backgroundColor: isLast ? PALETTE_AMBER : PALETTE_FOREST }}
        >
          {isLast ? (
            <>
              <Calculator className="h-4 w-4" aria-hidden />
              See my result
            </>
          ) : (
            <>
              Continue
              <ChevronRight className="h-4 w-4" aria-hidden />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
